import React, { useState } from 'react'
import styles from './EffConfigCard.module.scss'

type BadgeVariant = 'ok' | 'error' | 'info' | 'neutral'

function variantClass(variant: BadgeVariant): string {
  if (variant === 'ok') return styles.badgeOk
  if (variant === 'error') return styles.badgeError
  if (variant === 'info') return styles.badgeInfo
  return styles.badgeNeutral
}

// Badge is a generic colored chip. Use it whenever you need an inline label with
// one of the four semantic variants without reaching into another module's styles.
export function Badge({
  variant,
  children,
}: {
  variant: BadgeVariant
  children: React.ReactNode
}): JSX.Element {
  return <span className={`${styles.badge} ${variantClass(variant)}`}>{children}</span>
}

// BoolBadge renders a boolean as a colored chip. okWhenTrue tunes the semantics:
//   true  -> true is good (green), false is bad (red)   e.g. lumalink connected
//   false -> true is bad (red),   false is good (green) e.g. an error/alert flag
//   undefined -> neutral/info (no good/bad judgement)    e.g. lamp on/off
export function BoolBadge({
  value,
  okWhenTrue,
  trueLabel = 'Oui',
  falseLabel = 'Non',
}: {
  value: boolean | null
  okWhenTrue?: boolean
  trueLabel?: string
  falseLabel?: string
}): JSX.Element {
  if (value === null) return <span className={styles.valueNull}>n/a</span>
  const label = value ? trueLabel : falseLabel
  let variant: BadgeVariant
  if (okWhenTrue === true) {
    variant = value ? 'ok' : 'error'
  } else if (okWhenTrue === false) {
    variant = value ? 'error' : 'ok'
  } else {
    variant = value ? 'info' : 'neutral'
  }
  return <Badge variant={variant}>{label}</Badge>
}

export function NumericValue({ value, unit }: { value: number | null; unit?: string }): JSX.Element {
  if (value === null) return <span className={styles.valueNull}>n/a</span>
  return (
    <span className={styles.valueText}>
      {value}
      {unit ? ` ${unit}` : ''}
    </span>
  )
}

export function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className={styles.fieldRow}>
      <span className={styles.fieldLabel}>{label}</span>
      {children}
    </div>
  )
}

// EffConfigCard is the MetricCard-styled wrapper for one effective-config group.
// On hover it shows the same black "Capturé le: …" bubble as the metric cards.
export default function EffConfigCard({
  title,
  children,
  tooltip,
}: {
  title: string
  children: React.ReactNode
  tooltip?: string
}) {
  const [showTooltip, setShowTooltip] = useState(false)
  // Id stable pour relier la carte à son tooltip via aria-describedby, afin que
  // les lecteurs d'écran annoncent l'info « Capturé le : … » au focus.
  const tooltipId = React.useId()
  return (
    <div
      className={styles.card}
      tabIndex={0}
      role="group"
      aria-label={title}
      aria-describedby={tooltip ? tooltipId : undefined}
      onMouseEnter={() => tooltip && setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      onFocus={() => tooltip && setShowTooltip(true)}
      onBlur={() => setShowTooltip(false)}
    >
      <h3 className={styles.cardTitle}>{title}</h3>
      <div className={styles.cardBody}>{children}</div>
      {tooltip && showTooltip && (
        <div className={styles.tooltip} role="tooltip" id={tooltipId}>
          {tooltip}
        </div>
      )}
    </div>
  )
}
